import { useState, useCallback, MutableRefObject } from 'react';
import { PharmacyItem as FormPharmacyItem, PharmacyFormValues, calculateFromInclusiveMRP } from './dispenseHelper';
import { QRScanPharmacyResponse, useQRCodeScanner } from './pharmacyDispanseHelper';
import i18n from '../../services/i18n';

/**
 * Format date string to DD/MM/YYYY format
 */
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB');
};

/**
 * Map QR scan response to pharmacy form item
 */
export const mapQRScanToFormItem = (qrResponse: QRScanPharmacyResponse): FormPharmacyItem => {
  const baseMRP = Number(qrResponse.base_mrp) || 0;
  const baseCostPrice = Number(qrResponse.base_cost) || 0;
  const billedQty = 1;
  const totalMRPAmount = baseMRP * billedQty;
  
  // Build taxes array
  const baseTaxes: Array<{ name: string; percent: number; amount: number }> = [];
  const sgstPct = qrResponse.sgst_percent ?? 0;
  const cgstPct = qrResponse.cgst_percent ?? 0;
  if (sgstPct > 0) baseTaxes.push({ name: 'SGST', percent: sgstPct, amount: 0 });
  if (cgstPct > 0) baseTaxes.push({ name: 'CGST', percent: cgstPct, amount: 0 });
  
  // Calculate using tax-inclusive MRP formula
  const taxCalc = calculateFromInclusiveMRP(baseMRP, billedQty, baseTaxes);

  // Map batch_object to availableBatches format with taxes array
  const availableBatches = (qrResponse.batch_object || []).map((b) => {
    const bTaxes: Array<{ name: string; percent: number; amount: number }> = [];
    const bSgst = b.sgst_percent ?? sgstPct;
    const bCgst = b.cgst_percent ?? cgstPct;
    if (bSgst > 0) bTaxes.push({ name: 'SGST', percent: bSgst, amount: 0 });
    if (bCgst > 0) bTaxes.push({ name: 'CGST', percent: bCgst, amount: 0 });
    return {
      batchId: b.id || b.batch_id,
      batchNumber: b.batch_number || b.batch_code,
      expiryDate: formatDate(b.expiry_date),
      isExpired: new Date(b.expiry_date) < new Date(),
      baseMrp: b.base_mrp || baseMRP,
      baseCostPrice: b.base_cost || baseCostPrice,
      taxes: bTaxes,
      availableStock: b.available_stock || Number(qrResponse.closing_stock),
      availableUomId: b.available_uom_id || qrResponse.base_uom_id,
      availableUom: b.available_uom_name || qrResponse.base_uom_name,
      storeId: b.store_id || qrResponse.store_id,
    };
  });

  // Map uom_object to uomConversions format
  const uomConversions = (qrResponse.uom_object || []).map((uom) => ({
    id: uom.id ?? 0,
    item_id: uom.item_id || qrResponse.item_id,
    item_name: uom.item_name || qrResponse.item_name,
    uom_id: uom.uom_id,
    uom_code: uom.uom_code,
    uom: uom.uom_code,
    uom_static_field: uom.uom_static_field,
    code: uom.code,
    conversion_factor: uom.conversion_factor,
    converted_mrp: uom.converted_mrp,
    converted_cost_price: uom.converted_cost_price,
  }));

  return {
    prescriptionId: 0,
    itemId: qrResponse.item_id,
    visitDate: new Date().toLocaleDateString('en-GB'),
    itemCode: qrResponse.item_code,
    itemName: qrResponse.item_name,
    batchCode: qrResponse.batch_code,
    batchId: qrResponse.batch_id,
    expiryDate: formatDate(qrResponse.expiry_date),
    manufacturer: qrResponse.item_group || '',
    availableBatches: availableBatches,
    uomConversions: uomConversions,
    prescribedStrength: '',
    prescribedQty: 0,
    prescribedUOM: qrResponse.base_uom_name,
    prescribed_remaining_qty: 0,
    billedStrength: '',
    billedQty: billedQty,
    billedUOM: qrResponse.base_uom_name,
    rack: '',
    shelf: '',
    container: '',
    checked: true,
    baseMRP: baseMRP,
    baseCostPrice: baseCostPrice,
    totalMRPAmount: totalMRPAmount,
    sellingPricePerUnit: taxCalc.sellingPricePerUnit,
    totalSellingPrice: taxCalc.totalSellingPrice,
    concessionAmount: 0,
    concessionPercent: 0,
    taxes: taxCalc.taxesWithAmounts,
    netAmount: taxCalc.netAmount,
    pregnancyClass: '',
    provider: '',
    availableStock: Number(qrResponse.closing_stock) || 0,
    availableUomId: qrResponse.base_uom_id,
    availableUom: qrResponse.base_uom_name,
    storeId: qrResponse.store_id,
    tariffId: null,
    tariffName: undefined,
    packageId: null,
    packageName: undefined,
  };
};

/**
 * Options for useQRScanHandler hook
 */
export interface UseQRScanHandlerOptions {
  formikRef: MutableRefObject<{
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
    setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
    values: PharmacyFormValues;
  } | null>;
  clinicId: number;
  storeId: number | null;
  addAlert: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  showLoader: () => void;
  hideLoader: () => void;
}

/**
 * Return type for useQRScanHandler hook
 */
export interface UseQRScanHandlerReturn {
  isScanModeActive: boolean;
  scanInputRef: React.RefObject<HTMLInputElement>;
  scanInputValue: string;
  setScanInputValue: React.Dispatch<React.SetStateAction<string>>;
  handleScanSubmit: () => void;
  handleScanModeToggle: () => void;
}

/**
 * Custom hook that handles QR scanning logic for pharmacy dispense
 */
export function useQRScanHandler({
  formikRef,
  clinicId,
  storeId,
  addAlert,
  showLoader,
  hideLoader,
}: UseQRScanHandlerOptions): UseQRScanHandlerReturn {
  const tBilling = (key: string, defaultValue: string) => i18n.t(key, { ns: 'billing_config', defaultValue });
  const [isScanModeActive, setIsScanModeActive] = useState(false);

  /**
   * Handle QR scan success - add item to list
   */
  const handleQRScanSuccess = useCallback((response: QRScanPharmacyResponse) => {
    if (!formikRef.current) return;

    const currentItems = formikRef.current.values.items;

    // Check if batch already exists in the list
    const isBatchExists = currentItems.some(
      (item) => item.batchId === response.batch_id || item.batchCode === response.batch_code
    );

    if (isBatchExists) {
      addAlert(tBilling('pharmacy_dispense.messages.batch_already_in_list', 'This batch is already in the list'), 'warning');
      return;
    }

    // Map QR response to form item and add to list
    const newItem = mapQRScanToFormItem(response);
    formikRef.current.setFieldValue('items', [...currentItems, newItem]);
    addAlert(tBilling('pharmacy_dispense.messages.item_added_success', 'Item added successfully'), 'success');
  }, [formikRef, addAlert]);

  /**
   * QR Scanner Hook
   */
  const {
    scanInputRef,
    scanInputValue,
    setScanInputValue,
    handleScanSubmit,
  } = useQRCodeScanner({
    isActive: isScanModeActive,
    clinicId,
    storeId,
    onScanSuccess: handleQRScanSuccess,
    onScanError: (error) => addAlert(typeof error === 'string' ? error : error.message, 'error'),
    onClinicMismatch: () => addAlert(tBilling('pharmacy_dispense.errors.scanned_item_clinic_mismatch_current', 'Scanned item clinic does not match current clinic'), 'error'),
    onStoreMismatch: () => addAlert(tBilling('pharmacy_dispense.errors.scanned_item_store_mismatch_selected', 'Scanned item store does not match selected store'), 'error'),
    onLoadStart: showLoader,
    onLoadEnd: hideLoader,
  });

  /**
   * Handle scan mode toggle
   */
  const handleScanModeToggle = useCallback(() => {
    setIsScanModeActive((prev) => !prev);
  }, []);

  return {
    isScanModeActive,
    scanInputRef,
    scanInputValue,
    setScanInputValue,
    handleScanSubmit,
    handleScanModeToggle,
  };
}
